<template>
  <div
    class="design-system-input relative flex w-full items-center rounded-lg focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      :id="id ?? settingId"
      ref="input"
      v-model="modelValue"
      type="url"
      :pattern="pattern"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      class="grow border-0 bg-transparent p-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:ring-0"
    />
    <div
      class="flex shrink-0 items-center gap-3 p-2.5 py-1.5 pl-0 empty:hidden"
    >
      <PendingIndicator v-if="linkStatus === 'pending'" pending />
      <CheckCircleIcon
        v-else-if="linkStatus === 'ok'"
        class="size-5 text-green-500"
      />
      <Tooltip.Root v-else-if="linkStatus === 'not_ok'">
        <Tooltip.Trigger>
          <ExclamationCircleIcon class="size-5 text-red-500" />
        </Tooltip.Trigger>

        <Tooltip.Content>
          We can't seem to reach this URL, please check if they are correct.
        </Tooltip.Content>
      </Tooltip.Root>
      <Tooltip.Root v-else-if="linkStatus === 'errored'">
        <Tooltip.Trigger>
          <ExclamationCircleIcon class="size-5 text-red-500" />
        </Tooltip.Trigger>

        <Tooltip.Content>
          Something went wrong on our side and we weren't able to verify this
          URL, please check if they are correct.
        </Tooltip.Content>
      </Tooltip.Root>

      <slot name="icon" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, useTemplateRef, watch } from "vue";

import Urls from "@/autogen/urls";
import { useSettingControl } from "@/components/Setting/context";
import Tooltip from "@/design_system/Tooltip";
import CheckCircleIcon from "@/icons/heroicons/check-circle-mini.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-mini.svg";
import { useXsrfHeaders } from "@/lib/api/queries/utils";
import { LinkStatus } from "@/lib/link-checking/types";
import { useStore } from "@/store/stores/link-checker";

import PendingIndicator from "../PendingIndicator.vue";

const props = defineProps<{
  autofocus?: boolean;
  pattern?: string;
  placeholder?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}>();

const CHECKER_DEBOUNCE_DELAY = 2_000;

const modelValue = defineModel<string>({ default: "" });

const input = useTemplateRef("input");
onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

const setting = useSettingControl();
const settingId = setting?.register();

const headers = useXsrfHeaders();
const persister = useStore();
const inputTouched = ref(false);

const linkStatus = ref<"pending" | "errored" | "ok" | "not_ok">();

let deferred: Deferred<void> | undefined;

watch(
  [inputTouched, modelValue],
  async ([isInputTouched, urlString], [, prevUrlString], cleanup) => {
    // set up an inline-debounce mechanism, we create a promise that is either
    // resolved by our 2-second timeout, or by the focusout handler.
    if (isInputTouched && urlString !== prevUrlString) {
      const { promise, resolve } = (deferred = createDeferred());

      const timeout = setTimeout(resolve, CHECKER_DEBOUNCE_DELAY);
      cleanup(() => {
        deferred = undefined;
        clearTimeout(timeout);
      });

      // waiting... set the validation to blank as an indication to the user
      linkStatus.value = undefined;

      await promise;
    }

    // ignore empty input
    if (!urlString?.trim()) {
      linkStatus.value = undefined;
      return;
    }

    // parse the url, immediately quit if we can't parse it
    let url: URL;
    try {
      url = new URL(urlString);
    } catch {
      linkStatus.value = "not_ok";
      return;
    }

    const href = url.href;
    const protocol = url.protocol;

    // check if the URL is in our persisted store. if there is, use the results.
    {
      const persistedStatus = persister.getEntry(href);

      if (persistedStatus === LinkStatus.OK) {
        linkStatus.value = "ok";
        return;
      }
      if (persistedStatus === LinkStatus.NOT_OK) {
        linkStatus.value = "not_ok";
        return;
      }
    }

    // this is an URL input, so it'll be filled with an HTTP/S URL,
    // immediately quit if that's not the case
    if (protocol !== "https:" && protocol !== "http:") {
      linkStatus.value = "not_ok";
      return;
    }

    // if the user hasn't touched this input, stop now. (we might have lots
    // of URL inputs in a given settings page, we don't want them to fire all
    // at once for perf and traffic reasons.)
    if (!isInputTouched) {
      return;
    }

    // set up an abort controller for us to abort state updates
    const controller = new AbortController();
    const signal = controller.signal;

    cleanup(() => controller.abort());

    // now, we get to the link checking
    linkStatus.value = "pending";

    try {
      const response = await fetch(Urls["check-urls"](), {
        method: "POST",
        headers: {
          ...headers.value,
          "content-type": "application/json",
        },
        body: JSON.stringify({ urls: [href] }),
        signal,
      });

      if (!response.ok) {
        linkStatus.value = "errored";
        return;
      }

      const data = await response.json();

      if (!data.urls.includes(href)) {
        linkStatus.value = "ok";
        persister.setEntry(href, LinkStatus.OK);
      } else {
        linkStatus.value = "not_ok";
        persister.setEntry(href, LinkStatus.NOT_OK);
      }
    } catch {
      if (signal.aborted) {
        return;
      }

      linkStatus.value = "errored";
    }
  },
  { immediate: true }
);

const onFocusIn = () => {
  inputTouched.value = true;
};

const onFocusOut = () => {
  deferred?.resolve();
};

// `Promise.withResolvers` is not yet widely supported (89%)
// 2025-01-06: https://caniuse.com/mdn-javascript_builtins_promise_withresolvers
const createDeferred = <T>(): Deferred<T> => {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;

  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });

  return { promise, resolve, reject };
};

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}
</script>
